import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CIPXQuoteRequestIntakeForm, IntakeFormStep } from '../../intake-form.types';
import { SharedModule } from '@sbd-account-portal/app/shared/shared.module';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { findStageConfig, updateStageConfig, stages } from '../../steps/intake-form-bond-lookup/stage-config';
import { BondLookupUIControlStage } from '../../steps/intake-form-bond-lookup/intake-form-bond-lookup.types';
import { OpenQuoteRequestsWidgetService } from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import { UserService } from '@sbd-account-portal/app/core/user/user.service';

@Component({
  standalone: true,
  imports: [SharedModule, SharedComponentsModule],
  selector: 'cipx-quote-request-intake-form-confirmation',
  templateUrl: 'intake-form-confirmation.component.html'
})

export class CIPXQuoteRequestIntakeFormConfirmationComponent {
  private _intakeService = inject(CIPXQuoteRequestIntakeFormService);
  private _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);
  private _userService = inject(UserService);

  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  @Output() closeRequested = new EventEmitter<void>();

  completedQuoteRequest = this._intakeService.completedQuoteRequest;
  loggedInAccountUser = this._userService.currentAccountUser;
  selectedStateBond = this._intakeService.selectedStateBond;
  pricingCalculatorConfiguration = this._intakeService.pricingCalculatorConfiguration;
  selectedPenaltyOption = this._intakeService.selectedPenaltyOption;

  onRequestAnother() {
    // Fresh start - reset everything and go to Principal Selection
    this._intakeService.resetData();
    this._intakeService.completedQuoteRequest.set(null);

    // Reset all bond-lookup stages to defaults
    stages.forEach((stageConfig) => {
      updateStageConfig(stageConfig, stageConfig.defaultActive, true);
    });

    // Reset the reactive form values/touched/errors and defaults
    this._intakeService.resetForm.emit();

    // After reset completes, set the principal selection to 'myself'
    setTimeout(() => {
      const form = this.quoteRequestForm;
      const ps = form.controls.principalSelection;
      ps.controls.principalSelectionType.setValue('myself' as any, { emitEvent: true });
      ps.controls.existingPrincipal.setValue('');
    }, 100);

    this._intakeService.goToStep(IntakeFormStep.PrincipalSelection);
  }

  onRequestAnotherForThisPrincipal() {
    const completed = this.completedQuoteRequest();
    const userId = (completed as { user_id?: string | number } | null)?.user_id
      ?? this.quoteRequestForm.controls.user_id.value
      ?? null;
    const selectionType = this.quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself'
      ? 'myself'
      : 'existing';

    this._intakeService.resetData();
    this._intakeService.completedQuoteRequest.set(null);
    stages.forEach((stageConfig) => {
      updateStageConfig(stageConfig, stageConfig.defaultActive, true);
    });
    this._intakeService.resetForm.emit();

    setTimeout(() => {
      const ps = this.quoteRequestForm.controls.principalSelection;
      ps.controls.principalSelectionType.setValue(selectionType as 'myself' | 'existing', { emitEvent: false });

      // Bond Lookup must be current before apply so owner-count changes resize the array
      this._intakeService.goToStep(IntakeFormStep.BondLookup);

      if (!userId) return;

      this._intakeService.findPrincipalByUserId(userId, { useCache: false }).subscribe((rec) => {
        const accessRightId = rec?.account_user_access_right_id;
        if (accessRightId == null) return;

        if (selectionType === 'existing') {
          const match = this._intakeService.principals().find((p) => p.value === String(accessRightId));
          ps.controls.existingPrincipal.setValue(match ?? { value: String(accessRightId), label: '' });
        }

        this._intakeService.loadAndApplyPrincipalByAccessRightId(accessRightId, this.quoteRequestForm).subscribe();
      });
    }, 100);
  }

  onRequestAnotherWithSameBond() {
    // Only keep the selected state bond; clear Request Details and user context
    const sb = this.selectedStateBond();
    this._intakeService.completedQuoteRequest.set(null);

    // Preserve existing bondLookup values entirely; just ensure state bond id/selection align
    if (sb) {
      this._intakeService.selectedStateBond.set(sb);
      this.quoteRequestForm.get('state_bond_id')?.setValue(sb.state_bond_id);
      const bl = this.quoteRequestForm.get('bondLookup') as any;
      bl?.get('stateBondSelection')?.setValue(sb);
    }

    // Clear principal/owners/user context (leave bondLookup untouched)
    const requestDetails = this.quoteRequestForm.get('requestDetails');
    requestDetails?.get('principal')?.reset({
      company_name: '',
      company_dba: '',
      company_address_street_1: '',
      company_address_street_2: '',
      company_address_city: '',
      company_address_state_abbreviation: '',
      company_address_zip: '',
      company_address_county: '',
      company_address_phone: '',
    });
    requestDetails?.get('company_years_in_business')?.setValue(null);
    requestDetails?.get('addCompanyAddress')?.setValue(false);
    this._intakeService.companyNumberOwnersChanged.emit(1);
    this._intakeService.selectedPrincipalDetail.set(null);
    this.quoteRequestForm.get('user_id')?.setValue(null);

    this._intakeService.goToStep(IntakeFormStep.PrincipalSelection);
    // Do not alter stage config here since we preserved bondLookup values
  }

  onPurchaseNow() {
    this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe(() => {
      // todo: add purchase logic here when new drawer is implemented
    });
  }

  onDone() {
    this.closeRequested.emit();
  }

  getSelectedBondLabel(): string {
    const sb = this.selectedStateBond();
    if (!sb) {
      return '';
    }

    if (sb.state_bond_id === 1) {
      return this.quoteRequestForm.get('bondLookup.bond_family_label')?.value ?? '';
    }

    return `${sb.state_abbreviation} ${sb.surety_bond_label}`;
  }

  private setBondStagesFromCurrent(): void {
    // Ensure stages reflect the presence of a selected bond and penalty config
    const enable = (stage: BondLookupUIControlStage, state: boolean) => {
      const cfg = findStageConfig(stage, stages);
      if (cfg) {
        updateStageConfig(cfg, state);
      }
    };

    enable(BondLookupUIControlStage.State, true);
    enable(BondLookupUIControlStage.BondFamily, true);
    enable(BondLookupUIControlStage.StateBond, true);
    enable(BondLookupUIControlStage.Penalty, true);

    const hasOptions = !!(this.pricingCalculatorConfiguration()?.penalty?.options?.length);
    if (hasOptions) {
      enable(BondLookupUIControlStage.PenaltyOptions, true);
    }
    // Consider selection completed if we have a selected option or config type that doesn't require options
    const selectedOption = this.selectedPenaltyOption();
    const pit = this.pricingCalculatorConfiguration()?.penalty?.penalty_input_type;
    if (selectedOption || pit === 1 || pit === 3) {
      enable(BondLookupUIControlStage.BondSelectionCompleted, true);
    }
  }

  private setOnlyStateBondStages(): void {
    const enable = (stage: BondLookupUIControlStage, state: boolean) => {
      const cfg = findStageConfig(stage, stages);
      if (cfg) {
        updateStageConfig(cfg, state);
      }
    };
    // Ensure only up to StateBond is active
    enable(BondLookupUIControlStage.State, true);
    enable(BondLookupUIControlStage.BondFamily, true);
    enable(BondLookupUIControlStage.StateBond, true);
    enable(BondLookupUIControlStage.Penalty, false);
    enable(BondLookupUIControlStage.PenaltyOptions, false);
    enable(BondLookupUIControlStage.BondSelectionCompleted, false);
  }

  // Intentionally no ngOnInit lifecycle logic
}
